/** The shooters' rounds (projectiles.ts flies them): the ballistics per tower and per throwing unit. */

/**
 * How one shooter's round flies (art/DEV_HANDOFF.md §2). `release` is the `fire` clip frame (RELEASE_FPS) the round leaves on.
 * `launch`, when set, replaces `arc`: the elevation (radians) the round leaves at — the barrel's — with the lob sized
 * so the flight starts along it (ProjectileViews.lobFor).
 */
export interface Ballistics { model: string; kind: string; release: number; speed: number; arc: number; muzzle: string | null; launch?: number }

/**
 * Towers: model + vfx kind share the tower id; `muzzle` is the model part the round leaves from (views/towerMuzzle.ts
 * measures its mouth). The ballista has no `gun_tip`: its round leaves from the tip of the loaded `bolt`.
 */
/**
 * Release frames of the `fire` clips, the same keys at every tier, in the clips' own frames: art/hq_anim.py keys a
 * Blender frame f at f / 30 s (RELEASE_FPS). Until fin4/wp12b they were written as 24 fps frames (the handoff's rate),
 * which read the loose after the clip had already loosed — the arrow had flown on, the ball left a finished recoil.
 * The archer's arrow is back at its rest pose (where the muzzle is measured) at 7.5 and the clip hides it there
 * (tools/art/tower_clips.mjs); the ballista's bolt, drawn back to f6, passes its rest pose at 6.75 as the arms snap and
 * vanishes; the cannon dips forward to f2 and kicks back from there.
 */
const ARCHER_LOOSE = 7.5, BALLISTA_LOOSE = 6.75, CANNON_KICK = 2;
/** The Mortar's bore flash and kick (art hand-off 2026-10-04: `fire` 20 f, release f5). */
export const MORTAR_RELEASE = 5;
/** The cannon's round leaves at the barrel's own elevation plus the clip's kick (radians; ~9°). */
const CANNON_LAUNCH = 0.16;
/**
 * The loosed arrow is drawn at its modelled size, which is now the 0.35 m arrow the bow holds (proj_arrow was 0.48 m
 * until fin4/wp12b scaled the model to match). At the old default 1.6× it was 0.77 m and read as a second, bigger
 * arrow from nowhere (owner report 2026-09-23).
 */
export const ARROW_SCALE = 1;
export const TOWER_SHOT: Record<string, Ballistics> = {
  archer: { model: "proj_arrow", kind: "archer", release: ARCHER_LOOSE, speed: 11, arc: 0, muzzle: "gun_tip", launch: 0 },
  ballista: { model: "proj_bolt", kind: "ballista", release: BALLISTA_LOOSE, speed: 16, arc: 0, muzzle: "bolt" },
  cannon: { model: "proj_cannonball", kind: "cannon", release: CANNON_KICK, speed: 9, arc: 0, muzzle: "bore", launch: CANNON_LAUNCH },
  // Element rounds (vfx.js ELEMENT): the frost shard flies slower than an arrow so its streak and mist wake read (at 8
  // it crossed two cells in 0.25 s — a blink after the crystal's flash); the fireball is lobbed like a small mortar
  // (DESIGN §6.1) and the poison glob is spat in an arc; each lands on its one target (none of them has a splash).
  // Release frames: the frost core peaks on f9 (0.3 s), the fire nozzle on f8, the poison nozzle on f9.
  frost: { model: "proj_shard", kind: "frost", release: 9, speed: 5.5, arc: 0.12, muzzle: "core" },
  fire: { model: "proj_fireball", kind: "fire", release: 8, speed: 5, arc: 0.75, muzzle: "nozzle_tip" },
  poison: { model: "proj_glob", kind: "poison", release: 9, speed: 4.5, arc: 0.55, muzzle: "nozzle" },
  // The Mortar's shell (TOWERS_V2): released on f5 of its `fire` clip from the `bore`. It is not flown by
  // ProjectileViews: the sim lobs it at a landing point and lands it `shellFlightTicks` later, so views/mortarShells.ts
  // flies it on the sim's clock (speed and arc here are only the skin flourish's estimate of a typical lob).
  mortar: { model: "proj_shell", kind: "mortar", release: MORTAR_RELEASE, speed: 3.5, arc: 1.6, muzzle: "bore" },
};
/** Units with a sim event behind their throw: the bomber's bomb (BombDropped) and the healer's charm (EnemyHealed). */
export const UNIT_SHOT: Record<string, Ballistics> = {
  // 0.5 s and 0.54 s in, as tuned when these were written in 24 fps frames (12 and 13)
  bomber: { model: "proj_bomb", kind: "bomb", release: 15, speed: 3.5, arc: 1.1, muzzle: "bomb" },
  healer: { model: "proj_heal", kind: "heal", release: 16.25, speed: 6, arc: 0, muzzle: "weapon" },
};
/** The rate the clips are keyed at (art/hq_anim.py: Blender frame f at f / 30 s); `release` frames are in it. */
export const RELEASE_FPS = 30;
/** Seconds from the shot to the round leaving: the release frame, at the rate the firing clip plays (≥ 1). */
export function releaseSeconds(b: Ballistics, rate = 1): number { return b.release / RELEASE_FPS / Math.max(1, rate); }
